import type React from 'react';
import { Table } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useMemo, useState } from 'react';
import ChevronDownSmall from 'assets/svg/ChevronDownSmall';
import EditPencilOutlined from 'assets/svg/EditPencilOutlined';
import EditableMetricCell from './EditableMetricCell';
import type { MetricColumnConfig } from '../../../config/tpoGenConfig';
import type { PPGRow, TotalsRow } from '../../../types/promoMetrics';
import { computeTotals } from '../utils/computeTotals';
import { formatMetricValue } from '../utils/formatMetricValue';
import {
    TPO_PROMO_METRICS_TABLE,
    tpoMetricCellTestId,
    tpoMetricColTestId,
    tpoMetricRowTestId,
    tpoMetricTotalTestId,
} from '../../../config/testIds';

type TableRow = {
    key: string;
    product: string;
    children?: TableRow[];
    userEditedKeys: Set<string>;
    cascadedKeys: Set<string>;
    [metricKey: string]: unknown;
};

type CellHighlight = 'user-edited' | 'cascaded' | 'none';

function getCellHighlight(metricKey: string, record: TableRow): CellHighlight {
    if (record.userEditedKeys.has(metricKey)) return 'user-edited';
    if (record.cascadedKeys.has(metricKey)) return 'cascaded';
    return 'none';
}

const HIGHLIGHT_STYLES: Record<CellHighlight, React.CSSProperties> = {
    'user-edited': { backgroundColor: '#DBEAFE', borderLeft: '3px solid #60A5FA' },
    cascaded: { backgroundColor: '#FEF3C7', borderLeft: '3px solid #FBBF24' },
    none: {},
};

const toTableRow = (
    ppg: PPGRow,
    configs: MetricColumnConfig[],
    children?: TableRow[],
): TableRow => {
    const row: TableRow = {
        key: String(ppg.ppg_id),
        product: ppg.ppg_name,
        children,
        userEditedKeys: ppg.userEditedKeys,
        cascadedKeys: ppg.cascadedKeys,
    };
    for (const config of configs) {
        row[config.key] = ppg.values[config.key];
    }
    return row;
};

const buildTreeData = (ppgs: PPGRow[], configs: MetricColumnConfig[]): TableRow[] =>
    ppgs.map((ppg) => {
        const childRows = ppg.children?.map((child) => toTableRow(child, configs));
        return toTableRow(ppg, configs, childRows);
    });

const buildFlatData = (ppgs: PPGRow[], configs: MetricColumnConfig[]): TableRow[] =>
    ppgs.map((ppg) => toTableRow(ppg, configs));

const buildColumns = (
    configs: MetricColumnConfig[],
    isTreeView: boolean,
    expandedRowKeys: string[],
    onToggleExpand: (key: string) => void,
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void,
    showEditableIndicators: boolean,
): ColumnsType<TableRow> => [
    {
        title: 'Products',
        dataIndex: 'product',
        key: 'product',
        width: 270,
        fixed: 'left',
        onHeaderCell: () =>
            ({ 'data-testid': tpoMetricColTestId('product') } as React.ThHTMLAttributes<HTMLTableCellElement>),
        onCell: (record: TableRow) =>
            ({
                'data-testid': tpoMetricCellTestId(record.key, 'product'),
            } as React.TdHTMLAttributes<HTMLTableCellElement>),
        render: (text: string, record: TableRow) => {
            const hasChildren = isTreeView && record.children && record.children.length > 0;
            const isExpanded = expandedRowKeys.includes(record.key);
            const isChild = isTreeView && !record.children;

            return (
                <div className="flex items-center gap-2">
                    <span
                        className={`text-sm text-[#18181B] ${hasChildren ? 'font-medium' : 'font-normal'} ${
                            isChild ? 'pl-5' : ''
                        }`}
                    >
                        {text}
                    </span>
                    {hasChildren ? (
                        <span
                            className="cursor-pointer flex-shrink-0"
                            data-testid={`${tpoMetricRowTestId(record.key)}_expand`}
                            data-state-expanded={isExpanded ? 'true' : undefined}
                            onClick={() => onToggleExpand(record.key)}
                        >
                            <ChevronDownSmall className={isExpanded ? 'rotate-180' : 'rotate-0'} size={12} />
                        </span>
                    ) : null}
                </div>
            );
        },
    },
    ...configs.map((config) => ({
        title:
            showEditableIndicators && config.editable ? (
                <div className="flex items-center gap-1">
                    <span>{config.title}</span>
                    <EditOutlined />
                </div>
            ) : (
                config.title
            ),
        dataIndex: config.key,
        key: config.key,
        width: config.editable ? 140 : 120,
        onHeaderCell: () =>
            ({ 'data-testid': tpoMetricColTestId(config.key) } as React.ThHTMLAttributes<HTMLTableCellElement>),
        onCell: (record: TableRow) => {
            const highlight = getCellHighlight(config.key, record);
            return {
                'data-testid': tpoMetricCellTestId(record.key, config.key),
                // Signals which values the user changed and which the cascade
                // derived — the distinction QA needs when asserting cascades.
                'data-state-highlight': highlight === 'none' ? undefined : highlight,
                style: {
                    ...HIGHLIGHT_STYLES[highlight],
                    transition: 'background-color 0.3s, border-left 0.3s',
                },
            } as React.TdHTMLAttributes<HTMLTableCellElement>;
        },
        render: (value: number | undefined, record: TableRow) => {
            if (config.editable) {
                return (
                    <EditableMetricCell
                        value={value ?? 0}
                        config={config}
                        onCommit={(newValue) => onMetricChange(record.key, config.key, newValue)}
                        showEditableIndicator={showEditableIndicators}
                        testId={`${tpoMetricCellTestId(record.key, config.key)}_input`}
                    />
                );
            }
            return <span className="text-sm text-[#18181B]">{formatMetricValue(value, config)}</span>;
        },
    })),
];

type PromoMetricsTableProps = {
    metricConfigs: MetricColumnConfig[];
    isTreeView: boolean;
    ppgData: PPGRow[];
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void;
    showEditableIndicators?: boolean;
    /** When true, renders a sticky totals row pinned at the top of the scroll body. */
    showTotals?: boolean;
    /** BE-provided totals; when omitted, totals are derived from `ppgData`. */
    totals?: TotalsRow;
};

const PromoMetricsTable = ({
    metricConfigs,
    isTreeView,
    ppgData,
    onMetricChange,
    showEditableIndicators = false,
    showTotals = false,
    totals,
}: PromoMetricsTableProps) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

    const onToggleExpand = useCallback((key: string) => {
        setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    }, []);

    const columns = useMemo(
        () =>
            buildColumns(
                metricConfigs,
                isTreeView,
                expandedRowKeys,
                onToggleExpand,
                onMetricChange,
                showEditableIndicators,
            ),
        [metricConfigs, isTreeView, expandedRowKeys, onToggleExpand, onMetricChange, showEditableIndicators],
    );

    const dataSource = useMemo(
        () =>
            isTreeView
                ? buildTreeData(ppgData, metricConfigs)
                : buildFlatData(ppgData, metricConfigs),
        [isTreeView, metricConfigs, ppgData],
    );

    const totalsRow = useMemo(
        () => (showTotals ? totals ?? computeTotals(ppgData, metricConfigs) : null),
        [showTotals, totals, ppgData, metricConfigs],
    );

    const renderSummary = useCallback(
        () => (
            <Table.Summary fixed="top">
                <Table.Summary.Row className="bg-[#F4F4F5]">
                    <Table.Summary.Cell index={0}>
                        <span className="text-sm font-semibold text-[#18181B]">Total</span>
                    </Table.Summary.Cell>
                    {metricConfigs.map((config, i) => (
                        <Table.Summary.Cell index={i + 1} key={config.key}>
                            <span
                                className="text-sm font-semibold text-[#18181B]"
                                data-testid={tpoMetricTotalTestId(config.key)}
                            >
                                {formatMetricValue(totalsRow?.values[config.key], config)}
                            </span>
                        </Table.Summary.Cell>
                    ))}
                </Table.Summary.Row>
            </Table.Summary>
        ),
        [metricConfigs, totalsRow],
    );

    return (
        <Table<TableRow>
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content', y: 500 }}
            data-testid={TPO_PROMO_METRICS_TABLE}
            onRow={(record) =>
                ({ 'data-testid': tpoMetricRowTestId(record.key) } as React.HTMLAttributes<HTMLTableRowElement>)
            }
            expandable={{
                expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                expandIcon: () => null,
                indentSize: 0,
            }}
            summary={totalsRow ? renderSummary : undefined}
        />
    );
};

PromoMetricsTable.displayName = 'PromoMetricsTable';

export default PromoMetricsTable;
